import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { configService } from 'services/config.service';
import { createAppAsyncThunk } from 'slices/utils';
import { errorNotification } from 'utils/notifications';
import type { TField } from '@timeedit/types';
import {
  type TActivityDraftGroupConfig,
  ZActivityDraftGroupConfigSchemaFe,
} from '@timeedit/types/lib/types/activityGroup.type';
import api from '../services/api.service';
import type { TRootState } from './store';

// Define state interface with drawer properties
const validateActivityDraftGroupConfig = (
  config: TActivityDraftGroupConfig
) => {
  return ZActivityDraftGroupConfigSchemaFe.safeParse(config);
};

interface ActivityCreateTemplate {
  _id: string;
  name: string;
  fields: TField[];
  objects: { extId: string }[];
  informationFields: { label: string }[];
  objectFilters: { fieldExtId: string[]; label: string; typeExtId: string }[];
  primaryObject: { extId: string };
  activityType: { extId: string };
}

interface ObjectGenerationTemplateState {
  templates: TActivityDraftGroupConfig[];
  templatesMap: Record<string, TActivityDraftGroupConfig>;
  firstDrawer: TActivityDraftGroupConfig | null;
  secondDrawer: TActivityDraftGroupConfig | null;
  loading: boolean;
  error: string | null;
  activityCreateTemplates: ActivityCreateTemplate[];
  groupCreationMode: boolean | undefined;
}

// Initial state
const initialState: ObjectGenerationTemplateState = {
  templates: [],
  templatesMap: {},
  firstDrawer: null,
  secondDrawer: null,
  loading: false,
  error: null,
  activityCreateTemplates: [],
  groupCreationMode: false,
};

// Create slice with reducers
const objectGenerationTemplateSlice = createSlice({
  name: 'objectGenerationTemplate',
  initialState,
  reducers: {
    fetchTemplatesStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchTemplatesSuccess(
      state,
      action: PayloadAction<TActivityDraftGroupConfig[]>
    ) {
      state.templates = action.payload;
      state.loading = false;

      // Create templates map for quick lookups
      state.templatesMap = action.payload.reduce(
        (acc, template) => {
          acc[template.id] = template;
          return acc;
        },
        {} as Record<string, TActivityDraftGroupConfig>
      );
    },
    fetchTemplatesFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    // Update drawer template
    updateDrawerTemplate(
      state,
      action: PayloadAction<{
        isFirstDrawer: boolean;
        data: TActivityDraftGroupConfig | null;
        groupCreationMode?: boolean;
      }>
    ) {
      const { isFirstDrawer, data, groupCreationMode } = action.payload;
      if (groupCreationMode !== undefined) {
        state.groupCreationMode = groupCreationMode;
      }
      if (isFirstDrawer) {
        state.firstDrawer = data;
      } else {
        state.secondDrawer = data;
      }
    },
    // Delete template by ID
    deleteTemplateSuccess(state, action: PayloadAction<string>) {
      const deletedId = action.payload;

      // Clear drawers if they contained deleted templates
      if (state.firstDrawer && deletedId === state.firstDrawer.id) {
        state.firstDrawer = null;
      }
      if (state.secondDrawer && deletedId === state.secondDrawer.id) {
        state.secondDrawer = null;
      }
    },
    // Update template in store
    updateTemplateSuccess(
      state,
      action: PayloadAction<TActivityDraftGroupConfig>
    ) {
      const updatedTemplate = action.payload;

      // Update drawer if it contains this template
      if (state.firstDrawer?.id === updatedTemplate.id) {
        state.firstDrawer = { ...updatedTemplate, isEdited: false };
      }
      if (state.secondDrawer?.id === updatedTemplate.id) {
        state.secondDrawer = { ...updatedTemplate, isEdited: false };
      }
    },
    fetchActivityCreateTemplatesStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchActivityCreateTemplatesSuccess(
      state,
      action: PayloadAction<ActivityCreateTemplate[]>
    ) {
      state.activityCreateTemplates = action.payload;
      state.loading = false;
    },
    fetchActivityCreateTemplatesFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

// Export actions
export const {
  fetchTemplatesStart,
  fetchTemplatesSuccess,
  fetchTemplatesFailure,
  updateDrawerTemplate,
  deleteTemplateSuccess,
  updateTemplateSuccess,
  fetchActivityCreateTemplatesStart,
  fetchActivityCreateTemplatesSuccess,
  fetchActivityCreateTemplatesFailure,
} = objectGenerationTemplateSlice.actions;

// Handle discarding form changes
export const onDiscardForm = createAppAsyncThunk(
  'objectGenerationTemplate/onDiscardForm',
  async (
    { isFirstDrawer, id }: { isFirstDrawer: boolean; id: string | number },
    { dispatch, getState }
  ) => {
    const state = getState() as TRootState;
    const originalData = state.objectGenerationTemplate.templatesMap[id];

    dispatch(
      updateDrawerTemplate({
        isFirstDrawer,
        data: originalData ? { ...originalData, isEdited: false } : null,
      })
    );
  }
);

// Delete template
export const requestDeleteTemplate = createAppAsyncThunk(
  'objectGenerationTemplate/requestDeleteTemplate',
  async (id: string, { dispatch }) => {
    try {
      // API call would go here
      await api.delete({
        endpoint: `${configService.REACT_APP_BASE_URL}/activities/activity-draft-group-config/${id}`,
      });

      dispatch(deleteTemplateSuccess(id));
      dispatch(fetchTemplates());
      return true;
    } catch (error) {
      dispatch(fetchTemplatesFailure('Failed to delete template'));
      return false;
    }
  }
);

// Update template
export const requestUpdateTemplate = createAppAsyncThunk(
  'objectGenerationTemplate/requestUpdateTemplate',
  async (
    {
      isFirstDrawer,
      creationMode,
      data,
    }: {
      isFirstDrawer: boolean;
      creationMode: boolean | undefined;
      data: TActivityDraftGroupConfig;
    },
    { dispatch, getState }
  ) => {
    try {
      const dummyMongoDbIdToMakeValidationHappy = '696f2d4ae4454dd95ca3aea3';
      const validate = validateActivityDraftGroupConfig(
        creationMode
          ? {
              ...data,
              id: dummyMongoDbIdToMakeValidationHappy,
              _id: dummyMongoDbIdToMakeValidationHappy,
            }
          : data
      );

      if (!validate.success) {
        const errorMessages = validate.error.issues
          .map((issue) => {
            const field = issue.path.join('.') || 'general';
            return `• ${field}: ${issue.message}`;
          })
          .join('\n');
        errorNotification('Validation Failed', [errorMessages]);
        return;
      }

      let idOfNewGenerated: string | undefined = undefined;
      if (!data.id) {
        const res = await api.post({
          endpoint: `${configService.REACT_APP_BASE_URL}/activities/activity-draft-group-config`,
          data: {
            activityDraftGroupConfig: {
              ...data,
              id: undefined,
              _id: undefined,
            },
          },
        });
        if (res?.activityDraftGroupConfig?._id) {
          idOfNewGenerated = res.activityDraftGroupConfig._id;
        }
      } else {
        await api.patch({
          endpoint: `${configService.REACT_APP_BASE_URL}/activities/activity-draft-group-config/${data.id}`,
          data: { activityDraftGroupConfig: data },
        });
      }
      const dataCopy = idOfNewGenerated
        ? {
            ...data,
            isEdited: false,
            _id: idOfNewGenerated,
            id: idOfNewGenerated,
          }
        : { ...data, isEdited: false };
      dispatch(
        updateDrawerTemplate({
          isFirstDrawer,
          data: dataCopy,
          groupCreationMode: false,
        })
      );

      dispatch(fetchTemplates());

      return true;
    } catch (error) {
      dispatch(fetchTemplatesFailure('Failed to update template'));
      return false;
    }
  }
);

// Fetch templates thunk
export const fetchTemplates = createAppAsyncThunk(
  'objectGenerationTemplate/fetchTemplates',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(fetchTemplatesStart());
      const response = await api.get({
        endpoint: `${configService.REACT_APP_BASE_URL}/activities/activity-draft-group-config`,
      });
      const templates = response?.activityDraftGroupConfigs.map((config) => ({
        ...config,
        id: config._id,
        isEdited: false,
      }));
      dispatch(fetchTemplatesSuccess(templates));
    } catch (error) {
      dispatch(fetchTemplatesFailure('Failed to fetch templates'));
      return false;
    }
  }
);

export const fetchActivityCreateTemplates = createAppAsyncThunk(
  'objectGenerationTemplate/fetchActivityCreateTemplates',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(fetchActivityCreateTemplatesStart());
      const organizationId = getState().auth?.user?.organizationId;
      const response = await api.get<
        { _id: string; name: string; fields: TField[] }[]
      >({
        endpoint: `${configService.REACT_APP_BASE_URL}/activities/activity-create-template`,
      });

      dispatch(fetchActivityCreateTemplatesSuccess(response));
    } catch (error) {
      dispatch(
        fetchActivityCreateTemplatesFailure(
          'Failed to fetch activity create templates'
        )
      );
    }
  }
);

// Export reducer
export default objectGenerationTemplateSlice.reducer;

// Selectors
export const selectAllObjectGenerationTemplates = (state: TRootState) =>
  state.objectGenerationTemplate.templates;
export const selectTemplatesLoading = (state: TRootState) =>
  state.objectGenerationTemplate.loading;
export const selectTemplatesError = (state: TRootState) =>
  state.objectGenerationTemplate.error;
export const selectFirstDrawer = (state: TRootState) =>
  state.objectGenerationTemplate.firstDrawer;
export const selectSecondDrawer = (state: TRootState) =>
  state.objectGenerationTemplate.secondDrawer;
export const selectTemplatesMap = (state: TRootState) =>
  state.objectGenerationTemplate.templatesMap;
export const selectActivityCreateTemplates = (state: TRootState) =>
  state.objectGenerationTemplate.activityCreateTemplates;
export const selectGroupCreationMode = (state: TRootState) =>
  state.objectGenerationTemplate.groupCreationMode;

export const getListIdSelected = (state: TRootState) => {
  const { firstDrawer, secondDrawer } = state.objectGenerationTemplate;
  const result = [];
  if (firstDrawer) result.push(firstDrawer.id);
  if (secondDrawer) result.push(secondDrawer.id);
  return result;
};
